
import {defineComponent} from 'vue'
import {mapState} from "pinia";
import {useCustomerStore} from "@/stores/CustomerStore";
import {ErrorHandlerQueue} from "@/error/ErrorHandlerQueue";
import {DpExceptionsErrorHandler} from "@/error/handlers/DpExceptionsErrorHandler";
import {UIStateDto} from "@/dtos/UIStateDto";
import {AxiosErrorHandler} from "@/error/handlers/AxiosErrorHandler";
import {AbilityContext} from "@/context/AbilityContext";
import {PimProductsOverviewPageLayoutEnum} from "@/models/enum/pim/PimProductsOverviewPageLayoutEnum";
import {arrayHasContent} from "@/helpers/functions/arrays";
import {PimProductsPagingModel} from "@/models/paging/PimProductsPagingModel";
import BaseTitle from "@/components/UI/BaseTitle.vue";
import AlertError2 from "@/components/UI/Bootstrap/Alert/AlertError2.vue";
import OverviewTable from "@/components/layouts/catalog/products/CatalogProductsOverviewTable.vue";
import OverviewControls from "@/components/layouts/pim/PimProductsOverviewControls.vue";
import BaseContainer from "@/components/UI/Bootstrap/BaseContainer.vue";
import OverviewLegend from "@/components/layouts/pim/PimProductsOverviewLegend.vue";
import {CatalogProductsRestService} from "@/services/rest/cpc-mgmt/CatalogProductsRestService";
import OverviewCriteria from "@/components/layouts/catalog/products/CatalogProductsOverviewCriteria.vue";
import {AuthContext} from "@/context/AuthContext";
import {CpcExportRestService} from "@/services/rest/cpc-export/CpcExportRestService";
import {createConfirmDialog} from "vuejs-confirm-dialog";
import ConfirmModal from "@/components/UI/ConfirmModal.vue";
import {CatalogProductsCriteriaBodyRestDtoModel} from "@/models/criteria/CatalogProductsCriteriaBodyRestDtoModel";
import {I18nContext} from "@/context/I18nContext";
import {DpModuleEnum} from "@/api/enums/dp-module-enum";
import CatalogBuildStatus from "@/components/layouts/catalog/CatalogBuildStatus.vue";
import {
  PagedCatalogProductWithImageOverviewResultsRestDtoModel
} from "@/models/api/pharma-cpc-product-mgmt/PagedCatalogProductWithImageOverviewResultsRestDtoModel";

export default defineComponent({
  name: "CatalogProductsOverview",
  components: {
    CatalogBuildStatus,
    OverviewLegend,
    BaseContainer,
    OverviewControls,
    OverviewTable,
    AlertError2,
    BaseTitle,
    OverviewCriteria
  },
  props: {
    showBackRoute: {
      type: Boolean,
      required: false,
      default: false,
    }
  },
  data() {
    return {
      criteriaInitialized: false as boolean,
      criteriaChanged: false as boolean,

      productsOverview: PagedCatalogProductWithImageOverviewResultsRestDtoModel.createWithDefaults(),
      productsOverviewUI: UIStateDto.createWithDefaults(),

      isCustomSortWeightsChecked: false,
      isBulkActionsOpen: false,
    }
  },
  mounted() {
    if (this.getPimState.catalog_products_is_initialized) {
      this.reloadContent();
    } else {
      this.productsOverviewUI.setReady();
    }

    // Add click event listener to close accordion when clicking outside
    document.addEventListener('click', this.handleOutsideClick);
  },

  beforeUnmount() {
    // Remove event listener when component is destroyed
    document.removeEventListener('click', this.handleOutsideClick);
  },
  computed: {
    ...mapState(useCustomerStore, ["getCriteria", "getPaging", "getPimState"]),
    AuthContext() {
      return AuthContext
    },
    AbilityContext() {
      return AbilityContext
    },
    PimProductsOverviewPageLayoutEnum() {
      return PimProductsOverviewPageLayoutEnum;
    },
    productsCriteria(): CatalogProductsCriteriaBodyRestDtoModel {
      return this.getCriteria.catalog_products;
    },
    productsPaging(): PimProductsPagingModel {
      return this.getPaging.pim_products;
    },
    hasProducts(): boolean {
      return arrayHasContent(this.productsOverview.results);
    },
    goBackRoute(): object | null {
      if (this.showBackRoute) {
        return {
          name: 'sort-settings',
        };
      }

      return null;
    },
  },
  methods: {
    async exportProducts(): Promise<void> {
      this.productsOverviewUI
          .setNotReady()
          .clearError();
      try {
        // todo zorgen dat je export énkel kan triggeren als je criteria hebt ingevuld
        await CpcExportRestService.getInstance().exportCustomerCatalogDetailsXlsx(this.productsCriteria)
      } catch (exceptions: unknown) {
        this.setExceptions(exceptions);
      } finally {
        this.productsOverviewUI.setReady();
      }
    },
    async confirmExport(): Promise<void> {
      if ((this.productsOverview.paging_metadata?.total_elements ?? 0) <= 5000) {
        await this.exportProducts();
        return;
      }

      const {reveal, onConfirm} = createConfirmDialog(ConfirmModal, {
        title: this.$t('export'),
        body: this.$t('exportConfirm'),
        confirmButtonText: this.$t('export'),
        confirmButtonClass: 'btn btn-outline-primary',
      });

      onConfirm(async () => {
        await this.exportProducts();
      });

      await reveal();
    },
    async bulkClearCustomSortWeights(): Promise<void> {
      this.productsOverviewUI
          .setNotReady()
          .clearError();
      try {
        await CatalogProductsRestService.getInstance().bulkActionClearCustomSort(this.productsCriteria)
        await this.reloadContent();
      } catch (exceptions: unknown) {
        this.setExceptions(exceptions);
      } finally {
        this.productsOverviewUI.setReady();
      }
    },
    confirmBulkClearCustomSortWeights() {
      const {reveal, onConfirm} = createConfirmDialog(ConfirmModal, {
        title: this.$t('bulkClearCustomSortWeights'),
        body: this.$t('bulkClearConfirmation'),
        confirmButtonText: this.$t('confirm'),
        confirmButtonClass: 'btn btn-outline-primary',
      });

      onConfirm(async () => {
        this.closeAccordion();
        await this.bulkClearCustomSortWeights();
      });

      reveal();
    },
    async bulkClearCustomPrices(): Promise<void> {
      this.productsOverviewUI
          .setNotReady()
          .clearError();
      try {
        await CatalogProductsRestService.getInstance().bulkActionClearCustomStandardPrices(this.productsCriteria)
        await this.reloadContent();
      } catch (exceptions: unknown) {
        this.setExceptions(exceptions);
      } finally {
        this.productsOverviewUI.setReady();
      }
    },
    confirmBulkClearCustomPrices() {
      const {reveal, onConfirm} = createConfirmDialog(ConfirmModal, {
        title: this.$t('bulkClearCustomPrices'),
        body: this.$t('bulkClearConfirmation'),
        confirmButtonText: this.$t('confirm'),
        confirmButtonClass: 'btn btn-outline-primary',
      });

      onConfirm(async () => {
        this.closeAccordion();
        await this.bulkClearCustomPrices();
      });

      reveal();
    },
    async bulkClearCustomNamesNl(): Promise<void> {
      this.productsOverviewUI
          .setNotReady()
          .clearError();
      try {
        await CatalogProductsRestService.getInstance().bulkActionClearCustomNamesNl(this.productsCriteria)
        await this.reloadContent();
      } catch (exceptions: unknown) {
        this.setExceptions(exceptions);
      } finally {
        this.productsOverviewUI.setReady();
      }
    },
    confirmBulkClearCustomNamesNl() {
      const {reveal, onConfirm} = createConfirmDialog(ConfirmModal, {
        title: this.$t('bulkClearCustomNamesNl'),
        body: this.$t('bulkClearConfirmation'),
        confirmButtonText: this.$t('confirm'),
        confirmButtonClass: 'btn btn-outline-primary',
      });

      onConfirm(async () => {
        this.closeAccordion();
        await this.bulkClearCustomNamesNl();
      });

      reveal();
    },
    async bulkClearCustomNamesFr(): Promise<void> {
      this.productsOverviewUI
          .setNotReady()
          .clearError();
      try {
        await CatalogProductsRestService.getInstance().bulkActionClearCustomNamesFr(this.productsCriteria)
        await this.reloadContent();
      } catch (exceptions: unknown) {
        this.setExceptions(exceptions);
      } finally {
        this.productsOverviewUI.setReady();
      }
    },
    confirmBulkClearCustomNamesFr() {
      const {reveal, onConfirm} = createConfirmDialog(ConfirmModal, {
        title: this.$t('bulkClearCustomNamesFr'),
        body: this.$t('bulkClearConfirmation'),
        confirmButtonText: this.$t('confirm'),
        confirmButtonClass: 'btn btn-outline-primary',
      });

      onConfirm(async () => {
        this.closeAccordion();
        await this.bulkClearCustomNamesFr();
      });

      reveal();
    },
    showImportHistory() {
      this.$router.push({
        name: 'async-handlings',
        params: {
          moduleCode: DpModuleEnum.CPC,
          backRoute: 'catalog-products-overview'
        }
      });
    },
    onControlsChanged(): void {
      if (this.getPimState.catalog_products_is_initialized) {
        this.reloadContent();
      }
    },
    onCriteriaInitialized(): void {
      this.criteriaInitialized = true;
    },
    onCriteriaChanged(changed: boolean): void {
      this.criteriaChanged = changed;
    },
    onCategorizationSubmitted(): void {
    },
    async reloadContent(): Promise<void> {
      this.productsOverviewUI
        .setNotReady()
        .clearError();

      this.isCustomSortWeightsChecked = !!this.productsCriteria?.filter_is_custom_sort_weight_available;

      try {
        this.productsOverview = await CatalogProductsRestService.getInstance()
          .findCatalogProducts(this.productsCriteria, this.productsPaging, I18nContext.getDefaultLanguage());

        this.getPimState.catalog_products_is_initialized = true;
      } catch (exceptions: unknown) {
        this.setExceptions(exceptions);
      } finally {
        this.productsOverviewUI.setReady();
      }
    },
    setExceptions(exceptions: unknown): void {
      ErrorHandlerQueue
        .create()
        .add(DpExceptionsErrorHandler.createWithDefaultUIStateBehavior(this.productsOverviewUI as UIStateDto))
        .add(AxiosErrorHandler.createWithDefaultUIStateBehavior(this.productsOverviewUI as UIStateDto))
        .catch(exceptions, true);
    },

    closeAccordion(): void {
      this.isBulkActionsOpen = false;
      document.querySelector('#bulkActionsCollapse')?.classList.remove('show');
      document.querySelector('#bulkActionsHeader .accordion-button')?.classList.add('collapsed');
    },

    handleOutsideClick(event: MouseEvent): void {
      if (this.isBulkActionsOpen &&
          !document.querySelector('#bulkActionsAccordion')?.contains(event.target as Node)) {
        this.closeAccordion();
      }
    }
  }
})
